import {
  type RetailWarcraftTalentTree,
  type RetailWarcraftTalentTreeBlueprint,
  type RetailWarcraftTalentTreeBuildNode,
  type RetailWarcraftTalentTreeEntry,
  RetailWarcraftTalentTreeNodeType,
} from '@resources/js/typings/viewModels/retailWarcraftTalentTreeBuildNode';
import React, { useEffect, useMemo, useState, type JSX } from 'react';
import { Nodes, SvgGrid, treeWidth } from './TalentTreeFullInternals';
import TalentTreePercentBadge from '@resources/js/components/TalentTreePercentBadge/TalentTreePercentBadge';
import {
  heatMapPercentageClassName,
  heatMapPercentageForEntry,
} from '@resources/js/components/TalentTreeNode/TalentTreeNode';
import { ConditionalChoicesTooltip } from '@resources/js/components/ChoiceTalentTreeNode/ChoiceTalentTreeNode';
import PassiveTalentTreeNode from '@resources/js/components/PassiveTalentTreeNode/PassiveTalentTreeNode';

export interface HeroTreeProps {
  tree: RetailWarcraftTalentTree;
  blueprint: RetailWarcraftTalentTreeBlueprint;
  descriptionClassName: string;
  minWidthLarge?: boolean;
  expanded?: boolean;
  compact?: boolean;
  isDisplayingPercent?: boolean;
  spellModifierId: number;
  heroSpecId: number | null;
  icon: string;
  heroTreeOnly: boolean;
  HeroTitle?: ({
    treeId,
    treeName,
  }: {
    treeId?: number;
    treeName: string;
  }) => JSX.Element;
  ExportButton?: React.ReactNode;
}

const defaultHeroTitle: HeroTreeProps['HeroTitle'] = ({ treeName }) => (
  <>{treeName}</>
);

/**
 * A hero tree. Variant of `StandardTree` that handles the peculiarities of displaying a hero tree.
 */
export function HeroTree({
  tree,
  compact,
  expanded,
  minWidthLarge,
  spellModifierId,
  isDisplayingPercent,
  blueprint: fullBlueprint,
  descriptionClassName,
  heroSpecId,
  heroTreeOnly,
  HeroTitle = defaultHeroTitle,
  ExportButton,
}: HeroTreeProps): JSX.Element | null {
  const {
    blueprint: heroTreeBlueprint,
    subTreeNode,
    heroTreeId,
    selectHeroTree,
  } = useSelectedHeroTree(heroSpecId, tree, fullBlueprint);
  const horizontal = !minWidthLarge || heroTreeOnly;
  const isCompact = compact || isDisplayingPercent;

  const [widthData, heroTree, nodeCount] = useMemo(() => {
    let heroTree = onlyNodesForHeroTree(tree, heroTreeId);
    let widthData;
    if (isCompact) {
      heroTree = onlyChoiceNodes(heroTree, horizontal);
      widthData = horizontal
        ? { width: (heroTree.at(-1)?.length ?? 1) * 50, leftOffset: -25 }
        : { width: 2, leftOffset: -1 };
    } else {
      heroTree = withoutSubTreeNode(heroTree);
      widthData = treeWidth(heroTree);
    }

    return [
      widthData,
      heroTree,
      heroTree.map((row) => row.length).reduce((a, b) => a + b, 0),
    ];
  }, [isCompact, tree, horizontal, heroTreeId]);

  const rows = horizontal ? 1 : (heroTree.at(-1)?.at(0)?.node.row ?? 1);

  if (!heroTreeBlueprint || !subTreeNode) {
    return <div />;
  }

  const heroTreeEntry = subTreeNode.node.abilities.find(
    (entry) => entry.heroTreeId === heroTreeId,
  );

  const relativePct = heroTreeEntry
    ? heatMapPercentageForEntry(subTreeNode, heroTreeEntry)
    : 0;
  const heatMapClassName = heatMapPercentageClassName(relativePct);

  return (
    <div
      className={'talent-tree__container talent-tree__container--hero'}
      style={
        {
          '--rows': rows,
          '--talent-count': nodeCount /* used in horizontal mode */,
        } as React.CSSProperties
      }
    >
      {!heroTreeOnly && <div className={'talent-tree__hero-spacer'} />}
      <div
        className={'talent-tree__container--height-limiter'}
        data-kind={'hero'}
      >
        <div
          className={`talent-tree__hero-title do-not-change-color-on-hover ${descriptionClassName} ${
            horizontal ? 'talent-tree__hero-title--horizontal' : ''
          } ${nodeCount === 0 ? 'talent-tree__hero-title--no-nodes' : ''}`}
        >
          {HeroTitle && (
            <HeroTitle
              treeId={heroTreeId}
              treeName={heroTreeBlueprint.name.replace('-', '‑')}
            />
          )}
        </div>
        <div className={'talent-tree__hero-icon'}>
          <ConditionalChoicesTooltip
            node={subTreeNode}
            active={!!expanded && !!isDisplayingPercent && !heroTreeOnly}
            className={'talent-tree__implementation__hero-tooltip'}
            tooltipTriggerClassName={''}
            isDisplayingPercent={!!isDisplayingPercent}
            spellModifierId={spellModifierId}
            nodeComponent={(props) => (
              <HeroTreeSelectionNode
                {...props}
                onClick={(entry) => selectHeroTree(entry.heroTreeId)}
              />
            )}
          >
            <div
              className={`talent-tree__hero-icon--wrapper ${
                isDisplayingPercent
                  ? `talent-tree__hero-icon--rarity-${heatMapClassName}`
                  : ''
              }`}
            >
              <img src={heroTreeBlueprint.icon} alt={heroTreeBlueprint.name} />
            </div>
            {isDisplayingPercent ? (
              <TalentTreePercentBadge percent={relativePct} largeText={true} />
            ) : null}
          </ConditionalChoicesTooltip>
        </div>
        {nodeCount > 0 && (
          <div
            className={`talent-tree__hero-container ${
              compact || isDisplayingPercent
                ? 'talent-tree__hero-container--compact'
                : ''
            }
          ${horizontal ? 'talent-tree__hero-container--horizontal' : ''}
          `}
          >
            <div
              className={`talent-tree__implementation ${
                expanded ? 'talent-tree__implementation--expanded' : ''
              } ${
                isDisplayingPercent
                  ? 'talent-tree__implementation--heat-map'
                  : ''
              }`}
              data-expanded={expanded}
            >
              {!isCompact && (
                <SvgGrid
                  minWidthLarge={!!minWidthLarge}
                  tree={heroTree}
                  expanded={!!expanded}
                  rows={rows}
                  leftOffset={widthData.leftOffset}
                  width={widthData.width}
                />
              )}
              <Nodes
                tree={heroTree}
                expanded={!!expanded}
                minWidthLarge={!!minWidthLarge}
                leftOffset={widthData.leftOffset}
                treeWidth={widthData.width}
                spellModifierId={spellModifierId}
              />
            </div>
          </div>
        )}
        {!minWidthLarge && !heroTreeOnly && ExportButton}
      </div>
    </div>
  );
}

function useSelectedHeroTree(
  heroSpecId: number | null,
  tree: RetailWarcraftTalentTree,
  blueprint: RetailWarcraftTalentTreeBlueprint,
) {
  const realSubTreeNode = useMemo(
    () => tree.flat().find((node) => node.node.type === 'subtree'),
    [tree],
  );
  const initialId = useMemo(() => {
    return heroSpecId ? heroSpecId : realSubTreeNode?.entry?.heroTreeId;
  }, [heroSpecId, realSubTreeNode]);

  // eslint does not like `let` here because `setSelectedId` is never reassigned. `selectedId` is, though.
  // eslint-disable-next-line prefer-const
  let [selectedId, setSelectedId] = useState<number | undefined>(
    heroSpecId ?? undefined,
  );

  useEffect(() => {
    // reset to initial if the underlying tree changes.
    setSelectedId(initialId);

    // the eslint rule does not understand this usage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    selectedId = initialId; // assignment so that future code works as expected between renders. without this, future hooks would not see the update til the following render
  }, [tree, heroSpecId]);

  const heroBlueprint = useMemo(
    () => blueprint.heroTrees?.find((hero) => hero.id === selectedId),
    [selectedId, blueprint],
  );

  const subTreeNode = useMemo(
    () =>
      realSubTreeNode
        ? {
            ...realSubTreeNode,
            entry: realSubTreeNode?.node.abilities.find(
              (entry) => entry.heroTreeId === selectedId,
            ),
          }
        : undefined,
    [realSubTreeNode, selectedId],
  );

  return {
    subTreeNode,
    heroTreeId: selectedId,
    selectHeroTree: setSelectedId,
    blueprint: heroBlueprint,
  };
}

function withoutSubTreeNode(
  tree: RetailWarcraftTalentTree,
): RetailWarcraftTalentTree {
  return tree.map((row) =>
    row
      .filter((node) => node.node.type !== 'subtree')
      .map((node) => ({
        ...node,
        node: { ...node.node, row: node.node.row - 1 },
      })),
  );
}

function onlyChoiceNodes(
  tree: RetailWarcraftTalentTree,
  horizontal: boolean,
): RetailWarcraftTalentTree {
  const choiceTreeNodes = tree.flatMap((row) =>
    row.filter(
      (node) => node.node.type === RetailWarcraftTalentTreeNodeType.Choice,
    ),
  );

  if (horizontal) {
    return [
      choiceTreeNodes.map((node, ix) => ({
        ...node,
        node: {
          ...node.node,
          posX: ix * 50,
          row: 0.5,
        },
      })),
    ];
  } else {
    return choiceTreeNodes.map((node, ix) => [
      {
        ...node,
        node: {
          ...node.node,
          posX: 0,
          row: ix,
        },
      },
    ]);
  }
}

function onlyNodesForHeroTree(
  tree: RetailWarcraftTalentTree,
  heroTreeId: number | null | undefined,
): RetailWarcraftTalentTree {
  return heroTreeId
    ? tree.map((row) =>
        row.filter((node) => node.node.heroTreeId === heroTreeId),
      )
    : tree;
}

function HeroTreeSelectionNode({
  node,
  entry,
  spellModifierId,
  onClick,
}: {
  node: RetailWarcraftTalentTreeBuildNode;
  entry: RetailWarcraftTalentTreeEntry;
  spellModifierId: number;
  onClick: (entry: RetailWarcraftTalentTreeEntry) => void;
}): JSX.Element {
  return (
    <div
      onClick={(event) => {
        event.preventDefault();
        onClick(entry);
      }}
    >
      <PassiveTalentTreeNode
        node={node}
        entry={entry}
        spellModifierId={spellModifierId}
      />
    </div>
  );
}
